<template>
  <div class="photo-gallery-card" :data-aspect="aspectRatio" :data-image-id="imageId">
    <div class="card h-100 shadow-sm">
      <!-- Image Container -->
      <div class="image-container">
        <Image 
          ref="previewImage"
          :src="imageUrl" 
          :alt="imageTitle || 'Gallery Image'"
          class="card-img-top"
          @error="onImageError"
          preview
        />
        <!-- Image Overlay -->
        <div class="image-overlay" v-if="showOverlay">
          <div class="overlay-actions">
            <button 
              v-if="showViewButton" 
              type="button" 
              class="btn btn-light btn-sm overlay-btn preview-btn" 
              @click="onViewImage"
              :title="'Preview ' + (imageTitle || 'image')"
            >
              <i class="pi pi-image"></i>
            </button>
            <button 
              v-if="showDeleteButton" 
              type="button" 
              class="btn btn-danger btn-sm overlay-btn delete-btn" 
              @click="onDeleteImage"
              :disabled="isDeleting"
              :title="isDeleting ? 'Deleting...' : 'Delete ' + (imageTitle || 'image')"
            >
              <i v-if="!isDeleting" class="pi pi-trash"></i>
              <div v-else class="delete-spinner"></div>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Card Body -->
      <div class="card-body" v-if="showDetails">
        <h6 class="card-title" v-if="imageTitle">{{ imageTitle }}</h6>
        <p class="card-text" v-if="imageDescription">{{ imageDescription }}</p>
        <small class="text-muted" v-if="imageDate">{{ formatDate(imageDate) }}</small>
      </div>
      
      <!-- Card Footer -->
      <div class="card-footer bg-transparent" v-if="showFooter">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted" v-if="imageSize">{{ formatFileSize(imageSize) }}</small>
          <div class="btn-group" v-if="footerActions">
            <button 
              v-for="action in footerActions" 
              :key="action.name"
              type="button" 
              :class="'btn btn-sm ' + (action.class || 'btn-outline-secondary')"
              @click="action.handler"
              :title="action.title"
            >
              <i :class="action.icon"></i>
              <span v-if="action.text" class="ml-1">{{ action.text }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Image from 'primevue/image'

export default defineComponent({
  name: 'PhotoGalleryCard',
  components: {
    Image
  },
  props: {
    // Image properties
    imageUrl: {
      type: String,
      required: true
    },
    imageTitle: {
      type: String,
      default: ''
    },
    imageDescription: {
      type: String,
      default: ''
    },
    imageDate: {
      type: [String, Date],
      default: null
    },
    imageSize: {
      type: Number,
      default: null
    },
    imageId: {
      type: [String, Number],
      default: null
    },
    
    // Display options
    showDetails: {
      type: Boolean,
      default: true
    },
    showOverlay: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    
    // Action buttons
    showViewButton: {
      type: Boolean,
      default: true
    },
    showDeleteButton: {
      type: Boolean,
      default: false
    },
    
    // Loading state
    isDeleting: {
      type: Boolean,
      default: false
    },
    
    // Custom footer actions
    footerActions: {
      type: Array,
      default: () => []
    },
    
    // Aspect ratio for consistent image display
    aspectRatio: {
      type: String,
      default: '1:1', // Options: '1:1', '4:3', '16:9', '3:2'
      validator: (value) => ['1:1', '4:3', '16:9', '3:2'].includes(value)
    }
  },
  
  methods: {
    onViewImage() {
      // 1. Try to call internal preview handler (PrimeVue internal API) if available
      const comp = this.$refs.previewImage;
      if (comp && typeof comp.onImageClick === 'function') {
        comp.onImageClick();
      } else {
        // 2. Fallback: simulate click on underlying <img>
        const img = comp?.$el?.querySelector('img');
        if (img) img.click();
      }

      // (Optional) emit event in case parent wants to react (logging, analytics)
      this.$emit('view-image', {
        id: this.imageId,
        url: this.imageUrl,
        title: this.imageTitle
      });
    },
    
    onDeleteImage() {
      this.$emit('delete-image', {
        id: this.imageId,
        url: this.imageUrl,
        title: this.imageTitle,
        description: this.imageDescription
      })
    },
    
    onImageError(event) {
      // Use a data URL for a simple gray placeholder to avoid another network request
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='
      // Only emit error once per image
      if (!event.target.hasAttribute('data-error-handled')) {
        event.target.setAttribute('data-error-handled', 'true')
        this.$emit('image-error', {
          id: this.imageId,
          url: this.imageUrl,
          title: this.imageTitle
        })
      }
    },
    
    formatDate(date) {
      if (!date) return ''
      
      const d = new Date(date)
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    
    formatFileSize(bytes) {
      if (!bytes) return ''
      
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      if (bytes === 0) return '0 Bytes'
      
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    }
  }
})
</script>

<style scoped>
.photo-gallery-card {
  margin-bottom: 1rem;
  height: 100%;
  display: flex;
}

.card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.image-container {
  position: relative;
  overflow: hidden;
  background-color: #f8f9fa;
  flex-shrink: 0; /* Prevent image container from shrinking */
  height: 200px; /* Fixed height for consistent card structure */
}

.image-container::before {
  content: '';
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.card-img-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

/* PrimeVue Image component styling */
.image-container :deep(.p-image) {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.image-container :deep(.p-image img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-container :deep(.p-image img):hover {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6); /* Increased opacity for better contrast */
  backdrop-filter: blur(2px); /* Added blur effect */
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2; /* Ensure overlay is above image */
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.overlay-actions {
  display: flex;
  gap: 1.5rem; /* Increased gap between buttons for better spacing */
  align-items: center;
  justify-content: center;
  padding: 1rem; /* Added padding around the action buttons */
}

.overlay-actions .overlay-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  transition: all 0.3s ease;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.overlay-actions .overlay-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.overlay-actions .overlay-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
}

/* Preview Button Styling */
.overlay-actions .btn-light.overlay-btn.preview-btn {
  background-color: rgba(255, 255, 255, 0.9);
  color: #495057;
}

.overlay-actions .btn-light.overlay-btn.preview-btn:hover {
  background-color: #ffffff;
  color: #007bff;
}

.overlay-actions .btn-light.overlay-btn.preview-btn i.pi {
  font-size: 1.1rem;
  color: inherit;
}

/* Delete Button Styling */
.overlay-actions .btn-danger.overlay-btn.delete-btn {
  background-color: #dc3545;
  color: white;
  transition: all 0.3s ease;
}

.overlay-actions .btn-danger.overlay-btn.delete-btn:hover:not(:disabled) {
  background-color: #c82333;
  color: white;
}

.overlay-actions .btn-danger.overlay-btn.delete-btn:disabled {
  background-color: #dc3545;
  opacity: 0.8;
  cursor: not-allowed;
}

.overlay-actions .btn-danger.overlay-btn.delete-btn i.pi {
  font-size: 1.1rem;
  color: white;
}

.overlay-actions .btn-danger.overlay-btn.delete-btn .delete-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: delete-button-spin 1s linear infinite;
}

@keyframes delete-button-spin {
  to { transform: rotate(360deg); }
}

/* Ensure PrimeVue icons are visible */
.overlay-actions .overlay-btn i.pi {
  display: inline-block;
  text-align: center;
  line-height: 1;
  font-size: 1.1rem;
}


.card-body {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 140px; /* Increased minimum height for better consistency */
  justify-content: flex-start;
}

.card-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0; /* Don't shrink title */
  height: 1.4rem; /* Fixed height for title */
  line-height: 1.4rem;
  display: flex;
  align-items: center; /* Center title text vertically */
  margin-top: 0; /* Remove any default top margin */
}

.card-text {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.5rem;
  flex: 1; /* Take remaining space */
  min-height: 4.2rem; /* Space for exactly 3 lines */
  max-height: 4.2rem; /* Prevent expanding beyond 3 lines */
  line-height: 1.4;
  overflow-y: auto; /* Enable vertical scrolling */
  overflow-x: hidden; /* Hide horizontal overflow */
  padding-right: 0.25rem; /* Add space for scrollbar */
  text-align: justify; /* Justify text for better readability */
  word-wrap: break-word; /* Break long words */
  hyphens: auto; /* Enable hyphenation */
}

/* Custom scrollbar styling for description */
.card-text::-webkit-scrollbar {
  width: 4px;
}

.card-text::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.card-text::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.card-text::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.card-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e0e0e0;
  margin-top: auto; /* Push footer to bottom */
  flex-shrink: 0; /* Don't shrink footer */
  min-height: 3rem; /* Fixed minimum height for footer */
}

.text-muted {
  font-size: 0.7rem;
  color: #999;
  margin-bottom: 0;
  height: 1rem; /* Fixed height for date */
  line-height: 1rem;
  display: flex;
  align-items: center;
}

/* Aspect Ratio Variants */
.photo-gallery-card[data-aspect="1:1"] .image-container::before {
  --aspect-ratio: 100%;
}

.photo-gallery-card[data-aspect="4:3"] .image-container::before {
  --aspect-ratio: 75%;
}

.photo-gallery-card[data-aspect="16:9"] .image-container::before {
  --aspect-ratio: 56.25%;
}

.photo-gallery-card[data-aspect="3:2"] .image-container::before {
  --aspect-ratio: 66.67%;
}

/* Grid Support */
@media (min-width: 576px) {
  .photo-gallery-card {
    margin-bottom: 1.5rem;
  }
}

/* Responsive adjustments */
@media (max-width: 575px) {
  .card-body {
    padding: 0.75rem;
  }
  
  .overlay-actions .overlay-btn {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 0.9rem;
  }
  
  .overlay-actions {
    gap: 1rem;
  }
}
</style>